import React, { useState } from 'react';
import styles from '../../styles/ReporteSeguroAsesoresStyles/ReporteSeguroAsesores.module.css';
import { exportReporteSegurosAsesoresXLS } from '../../utils/exportReporteSegurosAsesoresXLS';
import { getReporteSegurosAsesores } from '../../api/reporteSegurosAsesores';

/**
 * Tabla del Reporte de Seguros de Asesores con paginación REAL en backend
 * (15 registros por página).
 *
 * Props:
 *  - resultados: Array<row> (sólo la página actual)
 *  - contexto:   { campus }
 *  - page:       número de página actual (base 1)
 *  - pageSize:   tamaño de página
 *  - total:      total de registros filtrados
 *  - totalPages: total de páginas
 *  - onPageChange(nextPage)
 *  - appliedFilters: filtros usados en la última generación (para export)
 *  - loading:    boolean
 */
function TablaReporteSegurosAsesores({
  resultados = [],
  contexto = {},
  page = 1,
  pageSize = 15,
  total = 0,
  totalPages = 1,
  onPageChange,
  appliedFilters,
  loading = false,
}) {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (total === 0 || exporting) return;
    setExporting(true);
    try {
      // Traer TODOS los registros filtrados (no sólo la página visible).
      const data = await getReporteSegurosAsesores({
        id_campus: appliedFilters?.id_campus || undefined,
        all: true,
      });
      exportReporteSegurosAsesoresXLS({
        resultados: data?.resultados || [],
        contexto,
      });
    } catch (e) {
      console.error('[TablaReporteSegurosAsesores] Error al exportar:', e);
      // eslint-disable-next-line no-alert
      alert('No se pudo exportar el reporte.');
    } finally {
      setExporting(false);
    }
  };

  const formatFecha = (iso) => {
    if (!iso) return '—';
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return iso;
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <>
      <div className={styles.reportHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h2 className={styles.reportTitle}>REPORTE DE SEGUROS DE ASESORES</h2>
        <button
          type="button"
          className={styles.generateButton}
          onClick={handleExport}
          disabled={total === 0 || exporting || loading}
          style={{ padding: '8px 16px', fontSize: 13 }}
        >
          {exporting ? 'Exportando...' : '📊 Exportar a Excel'}
        </button>
      </div>

      <div className={styles.reportMeta}>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Campus:</span>
          <span>{contexto.campus || 'Todos'}</span>
        </div>
        <div className={styles.reportMetaItem}>
          <span className={styles.reportMetaLabel}>Total asesores:</span>
          <span>{total}</span>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.reportTable}>
          <thead>
            <tr>
              <th className={styles.cellCenter} style={{ width: 50 }}>#</th>
              <th>Nombre</th>
              <th>RFC</th>
              <th>CURP</th>
              <th>Género</th>
              <th>Fecha de Nacimiento</th>
              <th className={styles.cellCenter}>Edad</th>
              <th className={styles.cellCenter}>Tiene Seguro</th>
              <th>Fecha de Seguro</th>
            </tr>
          </thead>
          <tbody>
            {resultados.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.cellCenter} style={{ padding: 20, color: '#999' }}>
                  No hay registros para mostrar.
                </td>
              </tr>
            ) : (
              resultados.map((r, idx) => {
                const consecutivo = (page - 1) * pageSize + idx + 1;
                return (
                  <tr key={`${r.id_asesor}-${idx}`}>
                    <td className={styles.cellCenter}>{consecutivo}</td>
                    <td>{r.nombre || '—'}</td>
                    <td>{r.rfc || '—'}</td>
                    <td>{r.curp || '—'}</td>
                    <td>{r.genero || '—'}</td>
                    <td>{formatFecha(r.fecha_nacimiento)}</td>
                    <td className={styles.cellCenter}>{r.edad ?? '—'}</td>
                    <td className={styles.cellCenter}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          background: r.tiene_seguro === 'SI' ? '#e6f4ea' : '#fdecea',
                          color: r.tiene_seguro === 'SI' ? '#1e7e34' : '#c5221f',
                        }}
                      >
                        {r.tiene_seguro || 'NO'}
                      </span>
                    </td>
                    <td>{r.tiene_seguro === 'SI' ? formatFecha(r.fecha_seguro) : '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > pageSize && (
        <div className={styles.pagination}>
          <span className={styles.pageInfo}>
            Mostrando {rangeStart}–{rangeEnd} de {total}
          </span>
          <div className={styles.pageButtons}>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => onPageChange && onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1 || loading}
            >
              « Anterior
            </button>
            <span className={styles.pageInfo}>Página {page} de {totalPages}</span>
            <button
              type="button"
              className={styles.pageButton}
              onClick={() => onPageChange && onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages || loading}
            >
              Siguiente »
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default TablaReporteSegurosAsesores;

